import { emit, on, prefersReducedMotion } from '@lucid/core';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';
import { addToCart } from '@lucid/card';

function mainPicker(scope) {
  const pickers = Array.from((scope || document).querySelectorAll('variant-picker'));
  return pickers.find((picker) => !picker.closest('product-card, quick-add-component, dialog, .lucid-card')) || null;
}

function openCart() {
  emit('cart:open');
  const drawer = document.querySelector('theme-drawer#cart-drawer, :is(theme-drawer, lucid-drawer)[data-lucid-drawer="cart"]');
  if (drawer && typeof drawer.open === 'function') {
    if (!drawer.isOpen) drawer.open();
    return;
  }
  if (!document.querySelector('cart-drawer-component')) {
    window.location.href = window.Theme?.routes?.cart_url || '/cart';
  }
}

/** Set panel: the page product plus ticked partner items, added to the cart in one request. Nothing is preselected. */
class LucidBundleSet extends HTMLElement {
  #offs = [];
  #cartTotal = 0;
  #productAvailable = true;

  connectedCallback() {
    this.#cartTotal = Number(this.dataset.cartTotal || 0);
    this.#productAvailable = this.dataset.available !== 'false';
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.#offs.push(
      on('product:variant', ({ detail }) => {
        if (detail?.scope && detail.scope !== document && !detail.scope.contains(this)) return;
        this.#syncCurrent(detail?.resource || null, detail?.available);
      }),
      on('size:state', ({ detail }) => {
        if (detail?.scope && detail.scope !== document && !detail.scope.contains(this)) return;
        this.#syncCurrent(null);
      })
    );
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#syncCurrent(null);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#offs.forEach((off) => off());
    this.#offs = [];
  }

  get #rows() {
    return Array.from(this.querySelectorAll('[data-bundle-row]'));
  }

  #isOn(row) {
    if (row.hasAttribute('data-current')) return true;
    return row.querySelector('[data-bundle-check]')?.getAttribute('aria-checked') === 'true';
  }

  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount_with_comma_separator}} €', this.dataset.currency || 'EUR');
  }

  #section() {
    return this.closest('.shopify-section') || document;
  }

  #currentVariantId() {
    const section = this.#section();
    const input = section.querySelector('product-form-component input[name="id"], form[action*="/cart/add"] input[name="id"]');
    return input instanceof HTMLInputElement ? input.value : '';
  }

  /** Variant of the page product: the checked size when the picker has one option, else the product form. */
  #pickedVariantId() {
    const picker = mainPicker(this.#section());
    if (picker && picker.querySelectorAll('fieldset').length === 1) {
      const checked = picker.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement && checked.dataset.variantId) return checked.dataset.variantId;
    }
    return this.#currentVariantId();
  }

  #unpicked() {
    const section = this.#section();
    if (section instanceof HTMLElement && section.dataset.lucidSize === 'none') return true;
    const picker = mainPicker(section);
    if (!picker) return false;
    return Array.from(picker.querySelectorAll('fieldset')).some(
      (fieldset) => fieldset.querySelector('input[type="radio"]') && !fieldset.querySelector('input[type="radio"]:checked')
    );
  }

  #currentSize() {
    const picker = mainPicker(this.#section());
    if (!picker || this.#unpicked()) return '';
    const checked = Array.from(picker.querySelectorAll('input[type="radio"]:checked')).map((input) => input.value);
    picker.querySelectorAll('select').forEach((select) => {
      const option = select.selectedOptions[0];
      if (option?.value) checked.push(option.textContent?.trim() || option.value);
    });
    return checked.join(' / ');
  }

  #syncCurrent(resource, available) {
    const row = this.querySelector('[data-bundle-row][data-current]');
    if (resource) {
      if (row && resource.id) row.dataset.variantId = String(resource.id);
      if (row && typeof resource.price === 'number') {
        row.dataset.price = String(resource.price);
        const price = row.querySelector('[data-bundle-price]');
        if (price) price.textContent = this.#money(resource.price);
      }
      this.#productAvailable = available !== undefined ? Boolean(available) : resource.available !== false;
    }
    const sizeText = row?.querySelector('[data-bundle-current-size]');
    const size = this.#currentSize();
    if (sizeText) {
      sizeText.textContent = size
        ? (this.dataset.textCurrent || '[size]').replace('[size]', size)
        : this.dataset.textCurrentNone || '';
    }
    if (row) {
      const checked = size ? mainPicker(this.#section())?.querySelector('input[type="radio"]:checked') : null;
      const variantId = checked instanceof HTMLInputElement ? checked.dataset.variantId || '' : '';
      row.querySelectorAll('[data-bundle-chip]').forEach((chip) => {
        const on = Boolean(variantId) && chip.dataset.variantId === variantId;
        chip.setAttribute('aria-checked', String(on));
      });
      const chips = Array.from(row.querySelectorAll('[data-bundle-chip]'));
      const focusable = chips.find((chip) => chip.getAttribute('aria-checked') === 'true') || chips[0];
      chips.forEach((chip) => (chip.tabIndex = chip === focusable ? 0 : -1));
      if (size) {
        const need = row.querySelector('[data-bundle-need]');
        if (need instanceof HTMLElement) need.hidden = true;
        this.#clearErrorIfComplete();
      }
    }
    this.#update();
  }

  #update() {
    let count = 0;
    let sum = 0;
    for (const row of this.#rows) {
      if (!this.#isOn(row)) continue;
      count += 1;
      sum += Number(row.dataset.price || 0);
    }
    const saving = Math.min(Math.max(Number(this.dataset.savingPercent || 0), 0), 90);
    const total = Math.round(sum * (1 - saving / 100));

    const countText = this.querySelector('[data-bundle-count]');
    if (countText) {
      countText.textContent =
        count === 1 ? this.dataset.textCountOne || '' : (this.dataset.textCountOther || '[count]').replace('[count]', String(count));
    }
    const totalText = this.querySelector('[data-bundle-total]');
    if (totalText) totalText.textContent = this.#money(total);
    const buttonPrice = this.querySelector('[data-bundle-add] .lucid-btn__price');
    if (buttonPrice) buttonPrice.textContent = this.#money(total);

    const shipText = this.querySelector('[data-bundle-ship-text]');
    const rate = this.dataset.convert === 'true' ? Number(window.Shopify?.currency?.rate || 1) : 1;
    const threshold = Math.round(Number(this.dataset.threshold || 0) * rate);
    if (shipText && threshold > 0) {
      const left = threshold - this.#cartTotal - total;
      shipText.textContent =
        left > 0 ? (this.dataset.textShipLeft || '').replace('[amount]', this.#money(left)) : this.dataset.textShipDone || '';
    }

    const button = this.querySelector('[data-bundle-add]');
    if (button instanceof HTMLButtonElement) button.disabled = !this.#productAvailable;
  }

  #setChecked(row, checked) {
    const check = row.querySelector('[data-bundle-check]');
    if (!check) return;
    check.setAttribute('aria-checked', String(checked));
    if (!checked) {
      const need = row.querySelector('[data-bundle-need]');
      if (need instanceof HTMLElement) need.hidden = true;
    }
  }

  #selectChip(chip) {
    const row = chip.closest('[data-bundle-row]');
    if (!(row instanceof HTMLElement) || chip.hasAttribute('disabled')) return;
    if (row.hasAttribute('data-current')) {
      this.#pickCurrent(chip);
      return;
    }
    row.querySelectorAll('[data-bundle-chip]').forEach((item) => {
      const on = item === chip;
      item.setAttribute('aria-checked', String(on));
      item.tabIndex = on ? 0 : -1;
    });
    row.dataset.variantId = chip.dataset.variantId || '';
    row.dataset.price = chip.dataset.price || row.dataset.price || '0';
    const price = row.querySelector('[data-bundle-price]');
    if (price) price.textContent = this.#money(Number(row.dataset.price));
    const need = row.querySelector('[data-bundle-need]');
    if (need instanceof HTMLElement) need.hidden = true;
    this.#setChecked(row, true);
    this.#clearErrorIfComplete();
    this.#update();
  }

  /** The page product's chips select the size in the main variant picker, which stays the single source. */
  #pickCurrent(chip) {
    const picker = mainPicker(this.#section());
    const input = picker?.querySelector(`input[type="radio"][data-variant-id="${CSS.escape(chip.dataset.variantId || '')}"]`);
    if (!(input instanceof HTMLInputElement)) return;
    chip.closest('[data-bundle-row]')?.querySelectorAll('[data-bundle-chip]').forEach((item) => {
      item.setAttribute('aria-checked', String(item === chip));
      item.tabIndex = item === chip ? 0 : -1;
    });
    if (input.checked) {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      input.click();
    }
  }

  #missingRows() {
    return this.#rows.filter((row) => {
      if (!this.#isOn(row)) return false;
      if (row.hasAttribute('data-current')) return this.#unpicked();
      return !row.dataset.variantId;
    });
  }

  #clearErrorIfComplete() {
    if (!this.#missingRows().length) this.#error('');
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const check = target.closest('[data-bundle-check]');
    if (check) {
      const row = check.closest('[data-bundle-row]');
      if (row) this.#setChecked(row, check.getAttribute('aria-checked') !== 'true');
      this.#update();
      return;
    }
    const chip = target.closest('[data-bundle-chip]');
    if (chip) {
      this.#selectChip(chip);
      return;
    }
    if (target.closest('[data-bundle-add]')) this.#add();
  };

  #onKeydown = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-bundle-chip]') : null;
    if (!chip || !['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const chips = Array.from(chip.parentElement?.querySelectorAll('[data-bundle-chip]:not(:disabled)') || []);
    const index = chips.indexOf(chip);
    if (index < 0 || !chips.length) return;
    event.preventDefault();
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = chips[(index + step + chips.length) % chips.length];
    next.focus();
    this.#selectChip(next);
  };

  #onCartUpdate = (event) => {
    const promise = event.promise;
    if (!promise || typeof promise.then !== 'function') return;
    promise
      .then(() => fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }))
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => {
        if (cart && typeof cart.total_price === 'number') {
          this.#cartTotal = cart.total_price;
          this.#update();
        }
      })
      .catch(() => {});
  };

  #error(message) {
    const element = this.querySelector('[data-bundle-error]');
    if (element) element.textContent = message || '';
  }

  async #add() {
    const button = this.querySelector('[data-bundle-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled || button.getAttribute('aria-busy') === 'true') return;
    this.#error('');

    const missing = this.#missingRows();
    if (missing.length) {
      missing.forEach((row) => {
        const need = row.querySelector('[data-bundle-need]');
        if (need instanceof HTMLElement) need.hidden = false;
      });
      const first = missing[0];
      const chip = first.querySelector('[data-bundle-chip]:not(:disabled)');
      if (chip instanceof HTMLElement) {
        this.#error(this.dataset.textPickRows || this.dataset.textPickSize || '');
        chip.focus();
      } else {
        this.#error(this.dataset.textPickSize || '');
        const picker = mainPicker(this.#section());
        picker?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
        emit('size:prompt', { source: 'bundle' });
      }
      return;
    }

    // Pre-order sizes of the product on the page carry the same order line note as the buy button
    const preorder = this.#section()?.querySelector('[data-preorder-variants]');
    const preorderIds = preorder?.getAttribute('data-preorder-variants') || '';
    const preorderName = preorder?.getAttribute('data-preorder-name') || '';
    const items = [];
    for (const row of this.#rows) {
      if (!this.#isOn(row)) continue;
      const id = row.hasAttribute('data-current') ? this.#pickedVariantId() || row.dataset.variantId || '' : row.dataset.variantId || '';
      if (!id) return;
      const properties =
        preorderName && preorderIds.includes(`,${id},`) ? { [preorderName]: preorder?.getAttribute('data-preorder-value') || '' } : null;
      items.push({ id, quantity: 1, ...(properties ? { properties } : {}) });
    }
    if (!items.length) return;

    button.setAttribute('aria-busy', 'true');
    try {
      await addToCart(items, { source: this, origin: 'lucid-bundle' });
      openCart();
    } catch (error) {
      this.#error((error?.handled && error.message) || this.dataset.textError || '');
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('lucid-bundle-set')) customElements.define('lucid-bundle-set', LucidBundleSet);

/**
 * Set offer on a single Cozy piece: one size row per piece of the bundle product. The page piece follows the main
 * variant picker, the other piece starts at its best-selling size; both fall back to the nearest size with an available
 * set combination. Adds the bundle variant.
 */
class LucidBundleOffer extends HTMLElement {
  /** @type {{id: number, options: string[], available: boolean, price: number}[]} */
  #variants = [];
  /** @type {string[]} */
  #values = [];
  /** @type {string[][]} */
  #order = [];
  #offs = [];

  connectedCallback() {
    try {
      this.#variants = JSON.parse(this.querySelector('[data-offer-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    this.#order = Array.from(this.querySelectorAll('[data-offer-option]')).map((piece) =>
      Array.from(piece.querySelectorAll('[data-offer-chip]')).map((chip) => chip.getAttribute('data-value') || '')
    );
    const page = Number(this.dataset.pageOption || 0);
    const other = page === 0 ? 1 : 0;
    this.#values = this.#order.map(() => '');
    this.#values[page] = this.#nearest(page, this.#pageSize() || this.dataset.pageDefault || '', '');
    this.#values[other] = this.#nearest(other, this.dataset.otherDefault || '', this.#values[page]);

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.#offs.push(
      on('product:variant', ({ detail }) => {
        if (detail?.scope && detail.scope !== document && !detail.scope.contains(this)) return;
        this.#followPage();
      }),
      on('size:state', ({ detail }) => {
        if (detail?.scope && detail.scope !== document && !detail.scope.contains(this)) return;
        this.#followPage();
      })
    );
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#offs.forEach((off) => off());
    this.#offs = [];
  }

  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat || '{{amount_with_comma_separator}} €', this.dataset.currency || 'EUR');
  }

  #pageSize() {
    const picker = mainPicker(this.closest('.shopify-section') || document);
    const checked = picker?.querySelector('fieldset input[type="radio"]:checked');
    return checked instanceof HTMLInputElement ? checked.value : '';
  }

  /** Whether a size of one piece has an available set, optionally together with a size of the other piece. */
  #has(index, value, otherValue = '') {
    const other = index === 0 ? 1 : 0;
    return this.#variants.some(
      (variant) => variant.available && variant.options[index] === value && (!otherValue || variant.options[other] === otherValue)
    );
  }

  /** The wanted size if it has an available set, else the nearest one in size order (the smaller one on a tie). */
  #nearest(index, wanted, otherValue) {
    const order = this.#order[index] || [];
    const norm = (value) => String(value).replace(/\s+/g, '').toLowerCase();
    let start = order.findIndex((value) => norm(value) === norm(wanted));
    if (start < 0) start = Math.floor((order.length - 1) / 2);
    for (let step = 0; step < order.length; step += 1) {
      for (const position of [start - step, start + step]) {
        const value = order[position];
        if (value !== undefined && this.#has(index, value, otherValue)) return value;
      }
    }
    return otherValue ? this.#nearest(index, wanted, '') : '';
  }

  #followPage() {
    const size = this.#pageSize();
    if (!size) return;
    const page = Number(this.dataset.pageOption || 0);
    const value = this.#nearest(page, size, '');
    if (!value || value === this.#values[page]) return;
    this.#choose(page, value);
  }

  #choose(index, value) {
    this.#values[index] = value;
    const other = index === 0 ? 1 : 0;
    if (!this.#has(other, this.#values[other], value)) {
      this.#values[other] = this.#nearest(other, this.#values[other], value);
    }
    this.#render();
  }

  get #variant() {
    return this.#variants.find(
      (variant) => variant.available && variant.options.every((option, index) => option === this.#values[index])
    );
  }

  #render() {
    this.querySelectorAll('[data-offer-option]').forEach((piece) => {
      const index = Number(piece.getAttribute('data-offer-option'));
      const other = index === 0 ? 1 : 0;
      const chips = Array.from(piece.querySelectorAll('[data-offer-chip]'));
      chips.forEach((chip) => {
        const value = chip.getAttribute('data-value') || '';
        const on = value === this.#values[index];
        const open = this.#has(index, value, this.#values[other]) || (!on && this.#has(index, value));
        chip.setAttribute('aria-checked', String(on));
        chip.tabIndex = on ? 0 : -1;
        chip.disabled = !open && !on;
        if (chip.disabled) chip.setAttribute('aria-label', chip.dataset.soldOutLabel || value);
        else chip.removeAttribute('aria-label');
      });
      if (!chips.some((chip) => chip.tabIndex === 0)) {
        const first = chips.find((chip) => !chip.disabled);
        if (first) first.tabIndex = 0;
      }
      const label = piece.querySelector('[data-offer-value]');
      if (label) label.textContent = this.#values[index] || '';
    });
    const variant = this.#variant;
    const button = this.querySelector('[data-offer-add]');
    if (button instanceof HTMLButtonElement) button.disabled = !variant;
    const price = this.querySelector('[data-offer-add] .lucid-btn__price');
    if (price && variant) price.textContent = this.#money(variant.price);
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const chip = target.closest('[data-offer-chip]');
    if (chip instanceof HTMLButtonElement && !chip.disabled) {
      const piece = chip.closest('[data-offer-option]');
      this.#choose(Number(piece?.getAttribute('data-offer-option') || 0), chip.getAttribute('data-value') || '');
      return;
    }
    if (target.closest('[data-offer-add]')) this.#add();
  };

  #onKeydown = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-offer-chip]') : null;
    if (!chip || !['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const chips = Array.from(chip.parentElement?.querySelectorAll('[data-offer-chip]:not(:disabled)') || []);
    const index = chips.indexOf(chip);
    if (index < 0) return;
    event.preventDefault();
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
    const next = chips[(index + step + chips.length) % chips.length];
    if (!(next instanceof HTMLButtonElement)) return;
    const piece = next.closest('[data-offer-option]');
    this.#choose(Number(piece?.getAttribute('data-offer-option') || 0), next.getAttribute('data-value') || '');
    next.focus();
  };

  async #add() {
    const button = this.querySelector('[data-offer-add]');
    const error = this.querySelector('[data-offer-error]');
    const variant = this.#variant;
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;
    if (!variant) {
      if (error) error.textContent = this.dataset.textPick || '';
      return;
    }
    if (error) error.textContent = '';
    button.setAttribute('aria-busy', 'true');
    try {
      await addToCart([{ id: variant.id, quantity: 1 }], { source: this, origin: 'lucid-bundle' });
      openCart();
    } catch (caught) {
      if (error) error.textContent = (caught?.handled && caught.message) || this.dataset.textError || '';
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('lucid-bundle-offer')) customElements.define('lucid-bundle-offer', LucidBundleOffer);
